<script setup lang="ts">
import { useTranslations } from '@/composables/useTranslations.ts'
import { computed, ref, watch } from 'vue'
import { usePageTitle } from '@/composables/usePageTitle.ts'
import { useUserStore } from '@/stores/user.store.ts'
import { storeToRefs } from 'pinia'
import StackedLineChart from '@/components/dashboard/StackedLineChart.vue'
import CrdButton from '@/components/common/crd-button/CrdButton.vue'
import ProjectStatisticsFilter from '@/components/dashboard/ProjectStatisticsFilter.vue'
import ProjectStatisticsTable from '@/components/dashboard/ProjectStatisticsTable.vue'
import type {
  ProjectStatisticsDataItemGraphqlResult,
  StatisticsChartData,
  StatisticsFilterEvent,
  StatisticsFilterParams,
  StatisticsTableDataItem,
} from '@/components/dashboard/dashboard.interfaces.ts'
import { ButtonVariant } from '@/components/common/crd-button/crd-button.enums.ts'
import { useQuery } from '@vue/apollo-composable'
import type { GraphQLOnResult } from '@/components/interfaces/graphql-common.interfaces.ts'
import { isNull } from 'lodash'
import { GetProjectStatisticsQuery } from '@/components/dashboard/graphql/dashboard.queries.ts'
import * as IProjects from '@/components/projects/interfaces.ts'
import { calculateConversion } from '@/utils/util.ts'
import dayjs from 'dayjs'
import CrdBlock from '@/components/common/CrdBlock.vue'
import CrdWrapper from '@/components/common/CrdWrapper.vue'
import CrdLabel from '@/components/common/CrdLabel.vue'

const { t } = useTranslations()
const userStore = useUserStore()
const { projectSelected } = storeToRefs(userStore)
const tableDataItems = ref<ProjectStatisticsDataItemGraphqlResult[]>([])
const isLoading = ref(false)

const title = computed(() => {
  const projectName = projectSelected?.value?.title || ''
  return projectName ? `${projectName} | ${t('navigation.analytics')}` : t('navigation.analytics')
})
usePageTitle(title)

function getDefaultDates(): { start: string | null; end: string | null } {
  const endDate = dayjs()
  const startDate = dayjs().subtract(3, 'month')
  return {
    start: startDate ? dayjs(startDate).format('YYYY-MM-DD') : '',
    end: endDate ? dayjs(endDate).format('YYYY-MM-DD') : '',
  }
}

const defaultDates = getDefaultDates()
const initFilter: StatisticsFilterParams = {
  fromDate: defaultDates.start || '',
  toDate: defaultDates.end || '',
  device: '',
}

const statisticsFilterRef = ref()
const filter = ref<StatisticsFilterParams>({ ...initFilter })

function setTableDataItems(dataList: ProjectStatisticsDataItemGraphqlResult[]): void {
  tableDataItems.value = dataList
}

function loadProjectStatisticsByProjectId(projectId: number): void {
  isLoading.value = true
  const { start, end } = getDefaultDates()
  const { onResult, onError } = useQuery(GetProjectStatisticsQuery, {
    input: {
      fromDate: filter.value.fromDate || start,
      toDate: filter.value.toDate || end,
      projectId: [projectId],
      // device: filter.value.device || null
    },
  })

  onResult((result: GraphQLOnResult<{ stats: ProjectStatisticsDataItemGraphqlResult[] }>) => {
    isLoading.value = false
    setTableDataItems(result.data.stats)
  })

  onError((error) => {
    console.log('onError:', error)
    isLoading.value = false
  })
}

function loadProjectStatisticsIfProjectExists(project: IProjects.Project | null): void {
  if (!isNull(project)) {
    loadProjectStatisticsByProjectId(project.id as number)
  }
}

function isDateInRange(itemDate: string, fromDate: Date | string, toDate: Date | string): boolean {
  const rowDate = new Date(itemDate)
  const start = fromDate && !isNaN(new Date(fromDate).getTime()) ? new Date(fromDate) : ''
  const end = toDate && !isNaN(new Date(toDate).getTime()) ? new Date(toDate) : ''
  const isAfterStart = start ? rowDate >= start : true
  const isBeforeEnd = end ? rowDate <= end : true

  return isAfterStart && isBeforeEnd
}

const filteredTableData = computed<StatisticsTableDataItem[]>(() => {
  const { fromDate, toDate } = filter.value
  return tableDataItems.value
    .map((item) => {
      const formattedDate = new Date(item.dateKey).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
      })

      return { ...item, formattedDate }
    })
    .filter((item) => isDateInRange(item.dateKey, fromDate, toDate))
})

const filteredChartData = computed<StatisticsTableDataItem[]>(() => {
  const { fromDate, toDate, device } = filter.value
  return tableDataItems.value
    .filter((item: ProjectStatisticsDataItemGraphqlResult) => {
      const dateMatch = isDateInRange(item.dateKey, fromDate, toDate)
      const deviceMatch = device ? item.device === device : true
      return dateMatch && deviceMatch
    })
    .map((item) => ({
      ...item,
      formattedDate: new Date(item.dateKey).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
      }),
    }))
})

const chartData = computed<StatisticsChartData>(() => {
  const grouped: Record<string, { chats: number; contacts: number }> = {}
  filteredChartData.value.forEach((item: StatisticsTableDataItem) => {
    const dateKey = item.dateKey
    if (!grouped[dateKey]) {
      grouped[dateKey] = { chats: 0, contacts: 0 }
    }
    grouped[dateKey].chats += item.chatsAmount
    grouped[dateKey].contacts += item.contactsAmount
  })
  const sortedDates = Object.keys(grouped).sort()
  const categories = sortedDates.map((date) =>
    new Date(date)
      .toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
      .replace('/', '.'),
  )

  return {
    categories,
    series: [
      { name: 'Chats', type: 'line', data: sortedDates.map((date) => grouped[date]?.chats ?? 0) },
      {
        name: 'Contacts',
        type: 'line',
        data: sortedDates.map((date) => grouped[date]?.contacts ?? 0),
      },
      {
        name: 'Conversion',
        type: 'line',
        data: sortedDates.map((date) => {
          const chats = grouped[date]?.chats ?? 0
          const contacts = grouped[date]?.contacts ?? 0
          const conversion = calculateConversion(chats, contacts)
          return conversion > 0 ? `${conversion} %` : '0'
        }),
      },
    ],
  }
})

const updateFilter = (newFilter: StatisticsFilterEvent) => {
  filter.value.fromDate = newFilter.fromDate
  filter.value.toDate = newFilter.toDate
}

const onReset = () => {
  filter.value = { ...initFilter }
  statisticsFilterRef.value?.resetFilter()
}

watch(
  projectSelected,
  (newProject) => {
    loadProjectStatisticsIfProjectExists(newProject)
  },
  {
    immediate: true,
  },
)
</script>

<template>
  <CrdWrapper>
    <div v-if="isLoading" class="p-4 text-center text-gray-600">{{ t('common.alerts.loading') }}...</div>
    <CrdBlock>
      <div class="flex flex-1 items-end gap-3">
        <div>
          <CrdLabel class="text-sm md:text-base font-semibold">{{ t('analytics.device') }}:</CrdLabel>
          <select
            v-model="filter.device"
            class="border rounded px-2 py-1 h-9 border-gray-300 min-w-32"
          >
            <option value="">{{ t('analytics.all') }}</option>
            <option value="desktop">{{ t('analytics.desktop') }}</option>
            <option value="mobile">{{ t('analytics.mobile') }}</option>
            <option value="tablet">{{ t('analytics.tablet') }}</option>
          </select>
        </div>
        <ProjectStatisticsFilter
          ref="statisticsFilterRef"
          @update:filter="updateFilter"
          :from-date="filter.fromDate"
          :to-date="filter.toDate"
        />
      </div>

      <div class="flex items-center gap-2">
        <CrdButton
          class="text-sm md:text-base font-semibold py-2 px-4"
          :variant="ButtonVariant.Cancel"
          @click="onReset"
          >Reset</CrdButton
        >
      </div>
    </CrdBlock>

    <CrdBlock class="h-80">
      <StackedLineChart :data="chartData" />
    </CrdBlock>

    <CrdBlock>
      <ProjectStatisticsTable :data="filteredTableData" />
    </CrdBlock>
  </CrdWrapper>

  <div class="pre-footer-text">
    {{ t('analytics.overviewDescription') }}
  </div>
</template>
